import { get, each, isNaN, isUndefined, groupBy, sum, isEmpty } from 'lodash';
import {
  QUESTION_MATRIX_OPEN_ENDED_STYLES,
  QUESTION_MATRIX_OPEN_ENDED_DECIMALS_DEFAULT,
  QUESTION_MATRIX_OPEN_ENDED_MIN_VALUE_DEFAULT,
  QUESTION_MATRIX_OPEN_ENDED_MAX_VALUE_DEFAULT,
  QUESTION_MATRIX_OPEN_ENDED_SCROLL_ANIMATION_TIME_HORIZONTAL,
  QUESTION_MATRIX_OPEN_ENDED_SCROLL_ANIMATION_TIME_VERTICAL,
  QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM_TOTAL_VALUE_DEFAULT,
  QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM_TYPES,
} from './constants';
import {
  QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM,
  QUESTION_MATRIX_OPEN_ENDED_NUMERIC,
} from '../../../../constants';
import { roundNumberToStringWithDecimals } from '../../../../utils';

export default function isValid(question) {
  if (
    question.style !== QUESTION_MATRIX_OPEN_ENDED_NUMERIC &&
    question.style !== QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM
  ) {
    return true;
  }

  const options = get(question, 'matrix.validation.openEndedValidation', {});
  const openEndedConfig = get(question, 'matrix.openEndedConfig', {});
  const openEndedStyle = openEndedConfig.style;
  const min = !isUndefined(options.min)
    ? options.min
    : QUESTION_MATRIX_OPEN_ENDED_MIN_VALUE_DEFAULT;
  const max = !isUndefined(options.max)
    ? options.max
    : QUESTION_MATRIX_OPEN_ENDED_MAX_VALUE_DEFAULT;
  const total = !isUndefined(options.total)
    ? options.total
    : QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM_TOTAL_VALUE_DEFAULT;
  const constantSumType =
    openEndedConfig.constantSumType ||
    QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM_TYPES.MUST_TOTAL;
  const allowedDecimals =
    options.decimals || QUESTION_MATRIX_OPEN_ENDED_DECIMALS_DEFAULT;
  const answerIds = [];
  const emptyAnswers = [];
  const emptyRequiredAnswers = [];
  let animationTime = QUESTION_MATRIX_OPEN_ENDED_SCROLL_ANIMATION_TIME_HORIZONTAL;

  if (openEndedStyle === QUESTION_MATRIX_OPEN_ENDED_STYLES.VERTICAL) {
    animationTime = QUESTION_MATRIX_OPEN_ENDED_SCROLL_ANIMATION_TIME_VERTICAL;
  }

  // iterate over all answers and get typed values
  each($('.answer--matrix-open-ended-answer-input'), (domElement) => {
    const answerText = $(domElement).val();
    const answerRow = $(domElement).attr('data-row-id');
    const answerIsNaN = isNaN(parseFloat(answerText));

    if (!answerIsNaN) {
      answerIds.push({ text: answerText, rowId: answerRow, domElement });
    } else {
      emptyAnswers.push(domElement);
    }

    const isRequired = $(domElement).attr('data-input-required') === 'true';
    if (isRequired && answerIsNaN) {
      emptyRequiredAnswers.push(domElement);
    }
  });

  if (emptyRequiredAnswers.length !== 0) {
    Pollfish.ui.prompt(Pollfish.ui.getText('warnings.matrixNumericOpenEnded'));
    // indicate all empty required fields with a red border
    each($(emptyRequiredAnswers), (domElement) => {
      $(domElement)
        .parents('.answer--matrix-open-ended-answer-input-container')
        .addClass('answer--matrix-open-ended-answer-input-container-required');
    });
    const answerWithoutSelection = emptyRequiredAnswers.shift();
    Pollfish.ui.scrollToElementPosition(answerWithoutSelection, animationTime);
    Pollfish.ui.matrixOpenEndedAnswerSlideDown(answerWithoutSelection);
    return false;
  }

  // at least one field should have been typed
  if (answerIds.length === 0) {
    Pollfish.ui.prompt(Pollfish.ui.getText('warnings.matrixNumericOpenEnded'));

    // get the first answer without value
    const answerWithoutSelection = emptyAnswers.shift();
    Pollfish.ui.scrollToElementPosition(answerWithoutSelection, animationTime);
    Pollfish.ui.matrixOpenEndedAnswerSlideDown(answerWithoutSelection);
    return false;
  }

  const valueNotBetweenMinMax = answerIds.filter(
    ({ text }) => parseFloat(text) < min || parseFloat(text) > max,
  );

  if (valueNotBetweenMinMax.length > 0) {
    const minWithDecimals = roundNumberToStringWithDecimals(
      min,
      allowedDecimals,
    );
    const maxWithDecimals = roundNumberToStringWithDecimals(
      max,
      allowedDecimals,
    );
    // indicate all fields where the value is not between min and max value with a red border
    each($(valueNotBetweenMinMax), ({ domElement }) => {
      $(domElement)
        .parents('.answer--matrix-open-ended-answer-input-container')
        .addClass('answer--matrix-open-ended-answer-input-container-required');
    });
    // show prompt for a couple of seconds for minimum and maximum accepted value
    Pollfish.ui.prompt(
      Pollfish.ui.getText('warnings.matrixNumericOpenEndedRange', {
        min: minWithDecimals,
        max: maxWithDecimals,
      }),
    );
    const invalidAnswerElement = valueNotBetweenMinMax.shift().domElement;
    Pollfish.ui.scrollToElementPosition(invalidAnswerElement, animationTime);
    Pollfish.ui.matrixOpenEndedAnswerSlideDown(invalidAnswerElement);
    return false;
  }

  if (question.style !== QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM) {
    return true;
  }

  const valueWithNegativeNumber = answerIds.filter(
    ({ text }) => parseFloat(text) < 0,
  );

  if (valueWithNegativeNumber.length > 0) {
    each($(valueWithNegativeNumber), ({ domElement }) => {
      $(domElement)
        .parents('.answer--matrix-open-ended-answer-input-container')
        .addClass('answer--matrix-open-ended-answer-input-container-required');
    });
    const text =
      Pollfish.ui.getText('matrixVanWestendorp.warnings.min', '0') || '';
    Pollfish.ui.prompt(text);
    const invalidAnswerElement = valueWithNegativeNumber.shift().domElement;
    Pollfish.ui.scrollToElementPosition(invalidAnswerElement, animationTime);
    Pollfish.ui.matrixOpenEndedAnswerSlideDown(invalidAnswerElement);
    return false;
  }

  // Here we continue the validations for Constant Sum
  const groupedByRow = groupBy(answerIds, 'rowId');
  const elementWithError = Object.values(groupedByRow).find((group) => {
    const groupRowValues = group.map(({ text }) => parseFloat(text));
    if (
      constantSumType ===
        QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM_TYPES.MUST_TOTAL &&
      sum(groupRowValues) !== total
    ) {
      return group;
    }
    return false;
  });

  if (elementWithError) {
    $('.answer--matrix-open-ended-info-message').addClass(
      'answer--matrix-open-ended-info-message-invalid',
    );
    const text =
      Pollfish.ui.getText('warnings.matrixConstantSumOpenEndedMustTotal') || '';
    Pollfish.ui.prompt(text.replace('XXXX', total));
    const invalidAnswerElement = elementWithError.shift().domElement;
    Pollfish.ui.scrollToElementPosition(invalidAnswerElement, animationTime);
    Pollfish.ui.matrixOpenEndedAnswerSlideDown(invalidAnswerElement);
    return false;
  }

  return true;
}

export function isRowValuesFilled(rowElement) {
  const inputElements = rowElement.querySelectorAll('input');
  let hasAnyEmptyValue = false;
  inputElements.forEach((input) => {
    if (hasAnyEmptyValue) {
      return;
    }
    hasAnyEmptyValue = isEmpty(input.value);
  });
  return !hasAnyEmptyValue;
}
